import { REGION_IDS, type RegionId } from "../shared/levels";
import type { RegionRecordView } from "../shared/types";
import { scopedStorage } from "../characters/scope";
import { ID_HASH_LEN, idHash48 } from "../shared/idHash";
import { WORLD_RULE_IDS, type WorldRuleId } from "../shared/content/worldRule";
// 로컬 저장(M1). 저장 실패(차단된 저장소·손상된 JSON)는 게임 진행을 막지 않는다.
// 여러 탭이 같은 키를 쓰므로, 쓸 때마다 저장소를 다시 읽어 합친 뒤 저장한다(골드는 증분, 기록은 최솟값·최댓값).
export const SAVE_KEY = "dungeonhero.save.v1";
export const SAVE_BACKUP_KEY = SAVE_KEY + '.bak';
export const SAVE_LATEST_BACKUP_KEY = SAVE_BACKUP_KEY + '.latest';
/** 마지막으로 성공한 정상 v1 쓰기의 사본(보상 writer 잠금 안에서 주 저장과 함께 쓴다). 주 저장이 손상되면 이것을 기준으로 복구한다 */
export const SAVE_GOOD_KEY = SAVE_KEY + '.good';
export interface SaveStorage { getItem(key: string): string | null; setItem(key: string, value: string): void; }
export interface SaveData { gold: number; bestClearTicks: number; maxCombo: number; }
/** v1의 선택 확장. 선택 완료 항목도 중복 지급 방지 영수증으로 남긴다. */
export interface RewardResult {
  id: string; region: RegionId; baseGold: number; clearTicks: number; maxCombo: number;
  stars: number; starMask: number; kills: number; damageDealt: number;
  cards: number[]; pickedCard: number;
  /** 보상 카드 내용(선택): 카드마다 'g:<등급>'(골드)·'s:…'(강화석)·'m:…'(보석)·'c:…'(소비)·'e:…'(장비), content/cardReward.ts. 없으면 전부 골드 카드(이전 영수증·이전 빌드) */
  cardLoot?: string[];
  /** 고른 카드(골드가 아닌 카드)를 장비 장부에 지급하고 장부 저장까지 확인했다(선택). 장부의 연산 기억(APPLIED_CAP)이 넘어가도 같은 영수증을 다시 지급하지 않는 표시 */
  cardGranted?: boolean;
  /** D3a: 깬 스테이지 id(스테이지 판만, 자유 입장은 없음). 진행 저장(stage.v1)을 다시 만들 때 판별 증거 — 이전 빌드 v1은 이 필드를 모르고 그대로 둔다 */
  stage?: string;
  /**
   * 결과 화면 보상 내역 표시 필드(선택, PR #175 리뷰 P2): 카드 고르기 전에 새로고침해도 같은 내역을 보이게 영수증에 함께 남긴다.
   * 이전 빌드·이전 영수증에는 없고(기본값으로 복원), 이전 빌드는 모르는 필드를 그대로 둔다. 골드 자체는 baseGold에 이미 합쳐져 있다
   */
  /**
   * 보너스 영수증(선택): 첫 클리어 골드를 영수증 없이 되돌려 줄 때 쓰는 고정 id(`owed:<스테이지>`) 기록. 골드는 baseGold로 같은 쓰기에 실려 가고,
   * 같은 id가 이미 있으면 다시 기록하지 않아 중복 지급이 없다. 지역 기록(클리어 수·별·최단)과 콤보에는 넣지 않는다
   */
  bonus?: boolean;
  /** 결과 화면 '스페셜 +N' 항목(선택): 골드는 처치 순간 이미 지급해 baseGold에 없고 표시용 금액만 남긴다 */
  specialGold?: number;
  clearMult?: number; firstGold?: number; firstStones?: number; firstStoneTier?: number; firstGear?: string[];
  /**
   * 레이드 주간 보상 영수증(RAID-PRD 2절, 선택 — 보너스 영수증): 레이드 id, 그 판 영웅(전설 무기 정의), 이번 주 몇 번째(1..),
   * 보호 주문서를 줬는지(0 = 강화석으로 바꿔 줌). 보상 연산은 이 값과 영수증 id로 다시 만들 수 있다(content/raidReward)
   */
  raid?: string; raidHero?: string; raidN?: number; raidProtect?: number;
  /** 세계 법칙(C10b, shared/content/worldRule WorldRuleId): 이 판에 걸린 규칙(수치가 있는 규칙만, 선택). 옛 빌드는 모르는 필드를 그대로 둔다 */
  rule?: string;
}
export interface RewardLocks { request(name: string, callback: () => void): Promise<unknown>; }
export interface SaveOptions { locks?: RewardLocks | null; }
export const REWARD_LOCK = 'dungeonhero.rewards.v1';
let resultSerial = 0;
export function rewardId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID() : `${Date.now()}-${++resultSerial}-${Math.random().toString(36).slice(2)}`;
}

export class MemoryStorage implements SaveStorage {
  private readonly values = new Map<string, string>();
  getItem(key: string): string | null { return this.values.get(key) ?? null; }
  setItem(key: string, value: string): void { this.values.set(key, value); }
}

function defaultStorage(): SaveStorage {
  // 지금 캐릭터가 있으면 그 캐릭터 키로(src/characters/scope.ts — 저장 모듈 전부가 이 저장소를 함께 쓴다), 없으면 원래 키
  try { if (typeof localStorage !== "undefined") return scopedStorage(localStorage); } catch {
    return { getItem() { throw new Error('저장소 차단'); }, setItem() { throw new Error('저장소 차단'); } };
  }
  return new MemoryStorage();
}
function valid(value: unknown): value is number { return typeof value === "number" && Number.isSafeInteger(value) && value >= 0; }

type SaveRecord = Record<string, unknown>;
type RegionRecord = { -readonly [K in keyof RegionRecordView]: RegionRecordView[K] };
/** 하드·헬 판 영수증인지(D3b: stage가 h<지역>-s<번호>, C10 헬: x<지역>-s<번호>) */
function isHardReceipt(r: RewardResult): boolean { return typeof r.stage === 'string' && /^[hx]\d-s\d\d$/.test(r.stage); }
/** 영수증 rule 필드를 읽는 유일한 길: WorldRuleId 화이트리스트에 있는 값만 돌려주고, 모르는 값(옛·새 빌드, 손상)은 null로 무시한다(영수증을 손상으로 보지 않는다) */
export function receiptRule(r: { readonly rule?: unknown } | null | undefined): WorldRuleId | null {
  const v = r?.rule;
  return typeof v === 'string' && (WORLD_RULE_IDS as readonly string[]).includes(v) ? (v as WorldRuleId) : null;
}
function regionRecord(): RegionRecord { return { clears: 0, bestClearTicks: 0, bestStars: 0 }; }
function object(value: unknown): value is SaveRecord { return value !== null && typeof value === "object" && !Array.isArray(value); }
const regionIds = REGION_IDS;
function best(a: number, b: number): number { return a === 0 ? b : b === 0 ? a : Math.min(a, b); }
function reward(value: unknown): value is RewardResult {
  if (!object(value) || typeof value.id !== 'string' || !value.id || !regionIds.includes(value.region as RegionId)) return false;
  for (const key of ['baseGold', 'clearTicks', 'maxCombo', 'stars', 'starMask', 'kills', 'damageDealt']) if (!valid(value[key])) return false;
  return (value.stars as number) <= 3 && (value.starMask as number) <= 7 &&
    Array.isArray(value.cards) && value.cards.length === 3 && value.cards.every(v => valid(v) && v <= 2147483647) &&
    Number.isInteger(value.pickedCard) && (value.pickedCard as number) >= -1 && (value.pickedCard as number) < 3;
}

/**
 * 보상 영수증 접기(D10, docs/PROGRESSION-720H.md 10절): 영수증이 RESULTS_FOLD_AT개를 넘으면 정산이 끝난 오래된 영수증부터 지워
 * RESULTS_KEEP개로 줄인다. 지운 수는 rewardResults.folded(진행 점수의 영수증 수), 지운 영수증의 스테이지 id는 rewardResults.stages(진행 복구 증거)에 남는다.
 * 지우는 것: 카드를 골랐고 장비 카드면 지급까지 끝난 영수증 중 — 무작위 결과 id(rewardId 형식), 고정 id 보너스 영수증(owed:·dl:·탑 등 — 골드는 이미 baseGold로 들어감),
 * 레이드 영수증(정산 표시가 확인된 것만, raidSettled). 고정 id는 그 id 자체가 중복 지급을 막으므로 지울 때 48비트 해시를 rewardResults.hashes에 남기고
 * hasResult·recordResult가 그 해시도 본다(같은 id는 영원히 한 번). 골드는 영수증과 따로 누적이라 지워도 바뀌지 않는다
 */
/**
 * 접기 기록(folded·hashes·stageRuns)이 있는 rewardResults의 version. 이전 빌드(1만 앎)는 접힌 고정 id 해시를 몰라 같은 id를 다시 지급하므로
 * 이 기록을 이어 쓸 수 없는 미래 형식으로 보고 save.v1을 쓰지 않는다(성장 원장 v3·장비 v4와 같은 규칙, PR #291 교차 리뷰 P1)
 */
export const RESULTS_FOLDED_VERSION = 2;
export const RESULTS_FOLD_AT = 30;
export const RESULTS_KEEP = 15;
/** 접은 영수증의 스테이지 id를 남기는 최대 수(노말·하드·헬 전 스테이지보다 넉넉히) */
export const FOLDED_STAGES_CAP = 400;
const RANDOM_RESULT_ID = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+-\d+-[0-9a-z]+)$/;

/** 이 빌드가 이어 쓸 수 있는 v1 기록인가(미래 버전·모르는 영수증 형식이면 false) — SaveStore와 캐릭터 이관(readLegacyState)이 같은 규칙을 쓴다 */
function writableSaveRecord(rec: SaveRecord): boolean {
  if (rec.version !== undefined && rec.version !== 1) return false;
  const ext = rec.rewardResults;
  return ext === undefined || (object(ext) && (ext.version === 1 || ext.version === RESULTS_FOLDED_VERSION) && object(ext.entries));
}
/**
 * 저장 원문 판정(SaveStore.read·readGood와 같은 규칙): ok = 이어 쓸 수 있는 v1 · future = 객체지만 이 빌드가 쓰지 않는 형식(덮어쓰지 않음, 사본 복구도 안 함) ·
 * corrupt = 파싱 불가·객체 아님(마지막 정상 사본 .good으로 잇는다)
 */
export function classifySaveRaw(raw: string): 'ok' | 'future' | 'corrupt' {
  try { const v: unknown = JSON.parse(raw); return object(v) ? (writableSaveRecord(v) ? 'ok' : 'future') : 'corrupt'; } catch { return 'corrupt'; }
}

export class SaveStore {
  readonly records = { r1: regionRecord(), r2: regionRecord(), r3: regionRecord(), r4: regionRecord(), r5: regionRecord(), r6: regionRecord() };
  private readonly confirmedRegions = { r1: regionRecord(), r2: regionRecord(), r3: regionRecord(), r4: regionRecord(), r5: regionRecord(), r6: regionRecord() };
  private readonly pendingRegions = { r1: regionRecord(), r2: regionRecord(), r3: regionRecord(), r4: regionRecord(), r5: regionRecord(), r6: regionRecord() };
  readonly data: SaveData = { gold: 0, bestClearTicks: 0, maxCombo: 0 };
  private readonly confirmed: SaveData = { gold: 0, bestClearTicks: 0, maxCombo: 0 };
  private pendingGold = 0;
  private pendingClearTicks = 0;
  private pendingCombo = 0;
  private pending = false;
  private readonly results = new Map<string, RewardResult>();
  private readonly pendingResults = new Map<string, RewardResult>();
  private readonly locks: RewardLocks | null | undefined;
  private flushing = false;
  private corruptRaw: string | null = null;
  /** 접어서 지운 고정 id 영수증의 해시(idHash48). hasResult가 본다 */
  private readonly foldedHashes = new Set<string>();
  /** 접어서 지운 영수증의 스테이지 id(저장 확인분, stageEvidence에 함께 낸다) */
  private readonly foldedStages = new Set<string>();
  /**
   * 접은 영수증의 스테이지별 판 수(rewardResults.stageRuns, 저장 확인분). 진행 복구(progress.clearedFromRecords)는 지역 클리어 수에서
   * 스테이지 영수증 수를 빼 이전 빌드 판을 가리므로 반복 클리어 수까지 남겨야 한다(PR #291 Codex P2)
   */
  private readonly foldedStageRuns = new Map<string, number>();
  /** 마지막으로 확인한 접은 영수증 수(rewardResults.folded). 오래된 .good으로 복구해 써도 줄지 않게 메모리 값과 합쳐 쓴다 */
  private foldedCount = 0;
  /** 레이드 영수증 id가 정산 표시까지 끝났는지(game.ts가 raidStore로 잇는다). 없으면 레이드 영수증은 접지 않는다 */
  raidSettled: ((id: string) => boolean) | null = null;
  /** 손상된 v1 원문을 백업(다시 읽어 일치 확인)한 뒤 이 탭의 마지막 정상 상태로 새로 이어 쓴 횟수 */
  recovered = 0;
  revision = 0;
  /** 마지막 저장 시도가 저장소에 기록됐는지(거부·용량 초과면 false, 메모리 값은 유지) */
  lastSaveOk = true;

  constructor(private readonly storage: SaveStorage = defaultStorage(), options: SaveOptions = {}) {
    // 브라우저에서는 모든 v1 쓰기를 같은 Web Lock으로 직렬화한다. 미지원은 쓰기를 보류한다.
    this.locks = options.locks !== undefined ? options.locks : typeof window === 'undefined'
      ? undefined : typeof navigator !== 'undefined' && navigator.locks ? navigator.locks : null;
    const rec = this.read();
    if (rec) this.absorb(rec);
    // 주 저장이 손상됐으면 정상 사본을 읽기만 해서 반영한다(복구 쓰기는 flushLocked가 잠금 안에서 한다).
    // 사본을 읽지 않으면 다음 정산 전까지 골드·지역 기록·미선택 카드가 0으로 보여 상점 구매가 막힌다.
    else if (this.corruptRaw !== null) { const good = this.readGood(); if (good) this.absorb(good); }
    this.project();
  }

  /** 프로필은 미저장 보상을 지출할 수 없다. 기존 보상 정산 의미는 유지한다. */
  get confirmedGold(): number { return this.confirmed.gold; }
  get backingStorage(): SaveStorage { return this.storage; }
  get savePending(): boolean { return this.pending || this.flushing; }

  getResult(id: string): RewardResult | undefined {
    const remote = this.results.get(id), local = this.pendingResults.get(id);
    return remote && remote.pickedCard >= 0 ? remote : local ?? remote;
  }

  /** 이 id의 영수증이 기록됐는지(남은 영수증 또는 접어서 지운 고정 id 해시, D10). 지급 여부 판단은 getResult 대신 이것을 쓴다 */
  hasResult(id: string): boolean {
    return this.getResult(id) !== undefined || (this.foldedHashes.size > 0 && this.foldedHashes.has(idHash48(id)));
  }

  /** 읽기 실패에는 현재 결과를 유지한다. 재시작/맵 이동에서만 호출한다. */
  refresh(): void {
    const rec = this.read();
    if (rec && this.writable(rec)) { this.absorb(rec); this.project(); }
  }

  /** 레이드 영수증 id(저장 확인분 + 미저장분, 'raid:'로 시작). 할당 없이 돈다 */
  forEachRaidReceipt(fn: (id: string) => void): void {
    for (const id of this.results.keys()) if (id.startsWith('raid:')) fn(id);
    for (const id of this.pendingResults.keys()) if (id.startsWith('raid:') && !this.results.has(id)) fn(id);
  }

  /** 보상 영수증에 남은 스테이지 id(D3a, 저장 확인분 + 미저장분). 진행 저장 이관의 증거 */
  stageEvidence(): string[] {
    const out: string[] = [];
    for (const st of this.foldedStages) { const n = Math.max(1, this.foldedStageRuns.get(st) ?? 1); for (let i = 0; i < n; i++) out.push(st); }
    for (const m of [this.results, this.pendingResults]) for (const r of m.values()) if (typeof r.stage === 'string' && r.stage) out.push(r.stage);
    return out;
  }

  unpickedResult(): RewardResult | undefined {
    for (const id of new Set([...this.results.keys(), ...this.pendingResults.keys()])) {
      const r = this.getResult(id);
      if (r?.pickedCard === -1) return r;
    }
    return undefined;
  }

  recordResult(result: RewardResult): void {
    if (!reward(result) || this.hasResult(result.id)) return;
    const rec: RewardResult = { ...result, cards: [...result.cards] };
    // 쓸 때는 화이트리스트 밖의 rule을 떼어 낸다(읽을 때의 무시는 receiptRule)
    if ('rule' in rec && receiptRule(rec) === null) delete rec.rule;
    this.pendingResults.set(result.id, rec);
    this.pending = true; this.project(); this.flush();
  }

  pickResult(id: string, index: number): void {
    this.refresh();
    const r = this.getResult(id);
    if (!r || r.pickedCard >= 0 || !Number.isInteger(index) || index < 0 || index >= 3) return;
    this.pendingResults.set(id, { ...r, cards: [...r.cards], pickedCard: index });
    this.pending = true; this.project(); this.flush();
  }

  /** 저장으로 확정된 영수증 중 고른 카드가 골드가 아닌데 장비 장부 지급이 끝나지 않은 것(미저장 선택은 제외 — 두 탭 중 먼저 저장된 선택만 확정) */
  ungrantedCards(): RewardResult[] {
    const out: RewardResult[] = [];
    for (const r of this.results.values()) {
      if (r.pickedCard < 0 || r.cardGranted || !Array.isArray(r.cardLoot)) continue;
      const d = r.cardLoot[r.pickedCard];
      if (typeof d !== 'string' || d === '' || d.startsWith('g:') || this.pendingResults.get(r.id)?.cardGranted) continue;
      out.push(r);
    }
    return out;
  }

  /** 지급 완료 표시를 적었지만 아직 저장으로 확정되지 않은 영수증이 있는지(있으면 정산은 끝난 게 아니라 재시도를 유지한다) */
  hasUnconfirmedCardGrant(): boolean {
    for (const [id, r] of this.pendingResults) if (r.cardGranted && !this.results.get(id)?.cardGranted) return true;
    return false;
  }

  /** 카드 지급 완료 표시를 영수증에 적는다(저장 확정된 영수증만, 같은 쓰기로 다른 필드는 그대로 둔다) */
  markCardGranted(id: string): void {
    const stored = this.results.get(id);
    if (!stored || stored.cardGranted) return;
    const local = this.pendingResults.get(id) ?? stored;
    this.pendingResults.set(id, { ...local, cards: [...local.cards], cardGranted: true });
    this.pending = true; this.project(); this.flush();
  }

  private writable(rec: SaveRecord): boolean { return writableSaveRecord(rec); }

  /** 지워도 되는 영수증인지(RESULTS_FOLD_AT 주석): 정산이 다 끝났고 같은 id가 다시 쓰일 일이 없다 */
  private foldable(id: string, r: unknown): boolean {
    if (!reward(r) || r.id !== id || r.pickedCard < 0 || this.pendingResults.has(id)) return false;
    if (id.startsWith('raid:')) { if (!this.raidSettled?.(id)) return false; }
    else if (!RANDOM_RESULT_ID.test(id) && r.bonus !== true) return false;
    const loot = Array.isArray(r.cardLoot) ? r.cardLoot[r.pickedCard] : undefined;
    return r.cardGranted === true || typeof loot !== 'string' || loot === '' || loot.startsWith('g:');
  }

  /**
   * 영수증이 RESULTS_FOLD_AT개를 넘으면 오래된(먼저 기록된) 정산 끝난 영수증부터 지워 RESULTS_KEEP개로 줄인다. 이 탭의 기억에서도 지운다.
   * 이 탭이 아는 접은 해시는 접기가 없는 쓰기에도 다시 합쳐 쓴다(오래된 .good 사본으로 복구해 써도 해시가 빠지지 않게)
   */
  private foldResults(ext: SaveRecord, entries: SaveRecord): void {
    const ids = Object.keys(entries);
    let n = 0;
    if (ids.length > RESULTS_FOLD_AT) {
      let drop = ids.length - RESULTS_KEEP;
      const stages = new Set<string>(Array.isArray(ext.stages) ? ext.stages.filter((v): v is string => typeof v === 'string') : []);
      const runs: SaveRecord = object(ext.stageRuns) ? { ...ext.stageRuns } : {};
      for (const id of ids) {
        if (drop <= 0) break;
        const r = entries[id];
        if (!this.foldable(id, r)) continue;
        const st = (r as RewardResult).stage;
        if (typeof st === 'string' && st) { stages.add(st); this.foldedStages.add(st); runs[st] = (valid(runs[st]) ? runs[st] as number : 0) + 1; this.foldedStageRuns.set(st, runs[st] as number); }
        // 고정 id(무작위 결과 id가 아님)는 다시 쓰일 수 있는 id라 해시를 남긴다
        if (!RANDOM_RESULT_ID.test(id)) this.foldedHashes.add(idHash48(id));
        delete entries[id];
        this.results.delete(id);
        n++; drop--;
      }
      if (n > 0) {
        ext.folded = (valid(ext.folded) ? ext.folded : 0) + n;
        this.foldedCount = Math.max(this.foldedCount, ext.folded as number);
        ext.stageRuns = runs;
        ext.stages = [...stages].sort().slice(0, FOLDED_STAGES_CAP);
      }
    }
    // 이 탭이 아는 접기 기록(수·스테이지·판 수)을 다시 합친다: 오래된 .good 사본으로 복구해 써도 줄지 않게(PR #291 교차 리뷰 P2)
    if (this.foldedCount > 0 && !(valid(ext.folded) && ext.folded >= this.foldedCount)) ext.folded = this.foldedCount;
    if (this.foldedStages.size > 0) {
      const st = new Set<string>(Array.isArray(ext.stages) ? ext.stages.filter((v): v is string => typeof v === 'string') : []);
      for (const x of this.foldedStages) st.add(x);
      ext.stages = [...st].sort().slice(0, FOLDED_STAGES_CAP);
      const runs: SaveRecord = object(ext.stageRuns) ? { ...ext.stageRuns } : {};
      for (const [x, k] of this.foldedStageRuns) if (!(valid(runs[x]) && (runs[x] as number) >= k)) runs[x] = k;
      ext.stageRuns = runs;
    }
    if (this.foldedCount > 0 || this.foldedHashes.size > 0 || this.foldedStages.size > 0) ext.version = RESULTS_FOLDED_VERSION;
    if (this.foldedHashes.size > 0) {
      const all = new Set<string>(this.foldedHashes);
      if (typeof ext.hashes === 'string') for (let i = 0; i + ID_HASH_LEN <= ext.hashes.length; i += ID_HASH_LEN) all.add(ext.hashes.slice(i, i + ID_HASH_LEN));
      ext.hashes = [...all].sort().join('');
    }
  }


  /** 클리어 기록: 기본 골드 지급, 최단 클리어 시간, 최대 콤보 */
  recordClear(gold: number, clearTicks: number, maxCombo: number, region?: RegionId, stars = 0): void {
    if (region) {
      const p = this.pendingRegions[region];
      p.clears++;
      if (valid(clearTicks)) p.bestClearTicks = best(p.bestClearTicks, clearTicks);
      if (valid(stars) && stars <= 3) p.bestStars = Math.max(p.bestStars, stars);
    }
    this.commit(gold, region && region !== "r1" ? 0 : clearTicks, maxCombo);
  }

  /** 골드만 추가(카드 보상) */
  addGold(gold: number): void {
    this.commit(gold, 0, 0);
  }

  private read(): SaveRecord | null | undefined {
    this.corruptRaw = null;
    let raw: string | null;
    try { raw = this.storage.getItem(SAVE_KEY); }
    catch { return undefined; } // 읽지 못한 저장을 빈 값으로 취급해 덮어쓰지 않는다.
    try {
      if (raw === null) return null;
      const parsed: unknown = JSON.parse(raw);
      if (object(parsed)) return parsed;
    } catch {
      // 손상 원문은 빈 저장이 아니다. 보상 writer 잠금 안에서 백업한 뒤 쓰기를 보류한다.
    }
    this.corruptRaw = raw;
    return undefined;
  }

  /**
   * flushLocked에서만 호출한다. 손상 원문을 최초(불변)·최신 백업 키에 남기고, 백업을 다시 읽어 같은 원문이 있을 때만 true.
   * true일 때만 주 저장을 새로 이어 쓸 수 있다. 백업이 차단·불일치하면 주 저장 원문을 그대로 둔다.
   */
  private backupCorrupt(raw: string): boolean {
    try {
      const first = this.storage.getItem(SAVE_BACKUP_KEY);
      if (first === null) this.storage.setItem(SAVE_BACKUP_KEY, raw);
      else if (first !== raw && this.storage.getItem(SAVE_LATEST_BACKUP_KEY) !== raw)
        this.storage.setItem(SAVE_LATEST_BACKUP_KEY, raw);
      return this.storage.getItem(SAVE_BACKUP_KEY) === raw || this.storage.getItem(SAVE_LATEST_BACKUP_KEY) === raw;
    } catch { return false; /* 백업도 차단되면 원문을 주 저장에 그대로 남기고 다음 정산에서 재시도한다. */ }
  }

  /** 마지막 정상 쓰기 사본. 없거나 손상·미래 버전이면 null(이 탭의 상태로 복구) */
  private readGood(): SaveRecord | null {
    try {
      const raw = this.storage.getItem(SAVE_GOOD_KEY);
      if (raw === null) return null;
      const parsed: unknown = JSON.parse(raw);
      return object(parsed) && this.writable(parsed) ? parsed : null;
    } catch { return null; }
  }

  private absorb(rec: SaveRecord): void {
    if (valid(rec.gold)) this.confirmed.gold = rec.gold;
    if (valid(rec.bestClearTicks)) this.confirmed.bestClearTicks = rec.bestClearTicks;
    if (valid(rec.maxCombo)) this.confirmed.maxCombo = rec.maxCombo;
    for (const id of regionIds) {
      const c = this.confirmedRegions[id], r = object(rec.regions) ? rec.regions[id] : undefined;
      // 지역 기록은 단조 병합한다(횟수·별은 최댓값, 시간은 최솟값). 저장소가 비거나 다른 탭이 지워도 이 탭이 확정한 기록은 남는다.
      if (object(r) && valid(r.clears)) c.clears = Math.max(c.clears, r.clears);
      if (object(r) && valid(r.bestClearTicks)) c.bestClearTicks = best(c.bestClearTicks, r.bestClearTicks);
      if (object(r) && valid(r.bestStars) && r.bestStars <= 3) c.bestStars = Math.max(c.bestStars, r.bestStars);
    }
    const ext = rec.rewardResults;
    if (this.writable(rec) && object(ext) && (ext.version === 1 || ext.version === RESULTS_FOLDED_VERSION) && object(ext.entries)) {
      for (const [id, value] of Object.entries(ext.entries)) if (reward(value) && value.id === id) this.results.set(id, value);
      if (Array.isArray(ext.stages)) for (const st of ext.stages) if (typeof st === 'string' && st.length > 0 && st.length < 40) this.foldedStages.add(st);
      if (valid(ext.folded) && ext.folded > this.foldedCount) {
        // 다른 탭이 새로 접었다: 이 탭 기억에 남은, 저장소에서 사라진 정산 끝 영수증은 접혀서 판 수(stageRuns)로 옮겨 갔다 — 기억에서 지워 두 번 세지 않는다
        // (PR #291 Codex P2). 오래된 .good을 읽는 복구 경로는 folded가 이 탭 값보다 크지 않아 지우지 않는다. 대기 중인 선택은 flushLocked가 따로 버린다
        const entries = ext.entries;
        for (const [id, r] of this.results) if (!Object.hasOwn(entries, id) && r.pickedCard >= 0 && !this.pendingResults.has(id)) this.results.delete(id);
        this.foldedCount = ext.folded;
      }
      if (object(ext.stageRuns)) for (const [st, n] of Object.entries(ext.stageRuns)) if (this.foldedStages.has(st) && valid(n)) this.foldedStageRuns.set(st, Math.max(this.foldedStageRuns.get(st) ?? 0, n));
      if (typeof ext.hashes === 'string') for (let i = 0; i + ID_HASH_LEN <= ext.hashes.length; i += ID_HASH_LEN) this.foldedHashes.add(ext.hashes.slice(i, i + ID_HASH_LEN));
    }
  }

  /** 아직 저장하지 못한 보상은 성공할 때까지 증분으로 보관한다. */
  private commit(goldDelta: number, clearTicks: number, maxCombo: number): void {
    if (valid(goldDelta)) this.pendingGold += goldDelta;
    if (valid(clearTicks) && clearTicks > 0 && (this.pendingClearTicks === 0 || clearTicks < this.pendingClearTicks)) this.pendingClearTicks = clearTicks;
    if (valid(maxCombo) && maxCombo > this.pendingCombo) this.pendingCombo = maxCombo;
    this.pending = true;
    this.flush();
  }

  private project(): void {
    const d = this.data, c = this.confirmed;
    d.gold = c.gold + this.pendingGold;
    d.bestClearTicks = c.bestClearTicks;
    if (this.pendingClearTicks > 0 && (d.bestClearTicks === 0 || this.pendingClearTicks < d.bestClearTicks)) d.bestClearTicks = this.pendingClearTicks;
    d.maxCombo = Math.max(c.maxCombo, this.pendingCombo);
    for (const id of regionIds) {
      const r = this.records[id], c = this.confirmedRegions[id], p = this.pendingRegions[id];
      r.clears = c.clears + p.clears;
      r.bestClearTicks = id === "r1" ? d.bestClearTicks : best(c.bestClearTicks, p.bestClearTicks);
      r.bestStars = Math.max(c.bestStars, p.bestStars);
    }
    for (const [id, local] of this.pendingResults) {
      const stored = this.results.get(id);
      if (!stored) {
        d.gold += local.baseGold;
        if (local.bonus) continue;
        d.maxCombo = Math.max(d.maxCombo, local.maxCombo);
        // 하드 판(D3b, 영수증 stage h…)은 골드·콤보만 쌓고 지역 기록(클리어 수·최단·별 — 노말 문 카드 '내 기록')에는 넣지 않는다.
        // 하드 기록은 하드 진행 저장(stage.hard.v1)에 따로 있다. 그래서 이전 빌드로 되돌려도 지역 기록이 하드 판으로 부풀지 않는다(PR #176 리뷰 P2)
        if (!isHardReceipt(local)) {
          if (local.region === 'r1') d.bestClearTicks = best(d.bestClearTicks, local.clearTicks);
          const r = this.records[local.region];
          r.clears++; r.bestStars = Math.max(r.bestStars, local.stars);
          r.bestClearTicks = best(r.bestClearTicks, local.clearTicks);
        }
      }
      if ((!stored || stored.pickedCard < 0) && local.pickedCard >= 0) d.gold += (stored ?? local).cards[local.pickedCard];
    }
  }

  /** 보상은 새로 만들지 않고 미저장분만 재시도한다. 다음 버전 필드도 보존한다. */
  flush(): boolean {
    if (!this.pending) return this.lastSaveOk;
    this.project();
    if (this.flushing) return false;
    if (this.locks === null) { this.lastSaveOk = false; return false; }
    if (this.locks) {
      this.flushing = true;
      try {
        void this.locks.request(REWARD_LOCK, () => { this.flushLocked(); }).catch(() => {
          this.lastSaveOk = false;
        }).finally(() => { this.flushing = false; this.revision++; });
      } catch { this.flushing = false; this.lastSaveOk = false; }
      return false;
    }
    return this.flushLocked();
  }

  private flushLocked(): boolean {
    let rec = this.read();
    // 손상 원문(파싱 불가·배열·null·스칼라·빈 문자열): 백업을 확인한 뒤에만 이어 쓴다. 기준은 마지막 정상 쓰기 사본(SAVE_GOOD_KEY,
    // 다른 탭이 확정한 보상 포함)이고, 사본이 없거나 읽을 수 없을 때만 이 탭의 마지막 정상 상태다. 미저장 보상은 그 위에 더한다.
    // 미래 버전(writable false)과 읽기 실패(getItem 예외)는 여기에 오지 않으며 계속 덮어쓰지 않는다.
    let recovering = false;
    if (this.corruptRaw !== null) {
      if (!this.backupCorrupt(this.corruptRaw)) { this.lastSaveOk = false; return false; }
      rec = this.readGood(); recovering = true;
    }
    if (rec === undefined || (rec && !this.writable(rec))) { this.lastSaveOk = false; return false; }
    const ext = rec?.rewardResults;
    const entries = object(ext) && object(ext.entries) ? ext.entries : {};
    // 복구할 때는 이 탭이 알지만 기준 사본에 없는 보상 영수증만 더해 중복 지급 방지를 이어 간다(사본의 카드 선택은 되돌리지 않는다).
    if (recovering) for (const [id, value] of this.results)
      if (!Object.hasOwn(entries, id)) Object.defineProperty(entries, id, { value, enumerable: true, writable: true, configurable: true });
    // 인식하지 못하는 항목과 같은 ID로 쓰지 않는다. 원문은 보존한다.
    for (const id of this.pendingResults.keys()) {
      if (Object.hasOwn(entries, id) && (!reward(entries[id]) || (entries[id] as RewardResult).id !== id)) {
        this.lastSaveOk = false; return false;
      }
    }
    // v1은 획득 전용(골드는 늘기만, 최단 시간은 줄기만, 콤보는 늘기만)이라 복구 때는 기준 사본과 이 탭의 확정값 중 더 앞선 값을 쓴다.
    const keep = recovering ? { ...this.confirmed } : null;
    if (rec) this.absorb(rec);
    // 잠금 안에서 방금 읽은 접은 해시로 미저장 영수증을 다시 거른다: 다른 탭이 같은 고정 id를 지급하고 접은 뒤 이 탭이 오래된 기억으로
    // 다시 넣으려 해도 골드가 두 번 들어가지 않게(PR #291 Codex 보안 P2). 무작위 결과 id는 다시 쓰이지 않아 해시를 남기지 않는다
    // 이 탭이 저장 확인했던 영수증(results)이 저장소에서 사라졌으면 다른 탭이 카드를 고른 뒤 접은 것이다(접기는 고른 영수증만 지운다).
    // 이 탭의 오래된 기억(pickedCard −1)으로 다시 고르면 카드 보상이 두 번이므로 대기 선택을 버린다(PR #291 Codex P1)
    // 버린 선택은 이 탭 기억에만 '정산 끝'(고른 카드·지급 완료)으로 남겨 결과 화면이 완료로 넘어가게 한다 — 저장하지 않고 골드도 더하지 않는다(PR #291 Codex P2)
    for (const id of [...this.pendingResults.keys()]) {
      const stored = this.results.get(id);
      if (!stored || Object.hasOwn(entries, id)) continue;
      const local = this.pendingResults.get(id)!;
      this.pendingResults.delete(id);
      this.results.set(id, { ...stored, cards: [...stored.cards], pickedCard: local.pickedCard >= 0 ? local.pickedCard : Math.max(0, stored.pickedCard), cardGranted: true });
    }
    if (this.foldedHashes.size > 0) {
      for (const id of [...this.pendingResults.keys()]) {
        if (!Object.hasOwn(entries, id) && !this.results.has(id) && !RANDOM_RESULT_ID.test(id) && this.foldedHashes.has(idHash48(id))) this.pendingResults.delete(id);
      }
    }
    if (keep) {
      const c = this.confirmed;
      c.gold = Math.max(c.gold, keep.gold); c.maxCombo = Math.max(c.maxCombo, keep.maxCombo);
      c.bestClearTicks = best(c.bestClearTicks, keep.bestClearTicks);
    }
    this.project();
    const d = this.data;
    const out: SaveRecord = rec ?? {};
    if (this.pendingResults.size > 0 || (recovering && this.results.size > 0)) {
      for (const [id, local] of this.pendingResults) {
        const stored = this.results.get(id);
        const next = stored ? { ...stored, pickedCard: stored.pickedCard >= 0 ? stored.pickedCard : local.pickedCard, ...(stored.cardGranted || local.cardGranted ? { cardGranted: true } : {}) } : local;
        Object.defineProperty(entries, id, { value: next, enumerable: true, writable: true, configurable: true });
      }
      out.rewardResults = { ...(object(ext) ? ext : {}), version: 1, entries };
      // foldResults가 접기 기록이 있으면 version을 RESULTS_FOLDED_VERSION으로 올린다
      this.foldResults(out.rewardResults as SaveRecord, entries);
    }
    out.gold = d.gold; out.bestClearTicks = d.bestClearTicks; out.maxCombo = d.maxCombo;
    for (const id of regionIds) {
      const regions = object(out.regions) ? out.regions : {};
      // 이번에 클리어한 지역, 또는 저장소에서 기록이 사라진(지워짐·손상) 지역만 쓴다. 메모리 기록이 0이면 건드리지 않는다.
      const stored = regions[id], mem = this.records[id];
      // 횟수·최고 별·(r2) 최단 시간 중 하나라도 저장이 메모리보다 뒤처지면 다시 쓴다
      const lost = mem.clears > 0 && !(object(stored) && valid(stored.clears) && stored.clears >= mem.clears
        && valid(stored.bestStars) && stored.bestStars >= mem.bestStars
        && (id === 'r1' || (valid(stored.bestClearTicks) && best(stored.bestClearTicks, mem.bestClearTicks) === stored.bestClearTicks)));
      if (this.pendingRegions[id].clears === 0 && !lost) continue;
      const record = object(regions[id]) ? regions[id] : {};
      record.clears = this.records[id].clears; record.bestStars = this.records[id].bestStars;
      // r1 최단 시간은 최상위 bestClearTicks(기존 v1), r2~r4는 지역 기록에
      if (id !== "r1") record.bestClearTicks = this.records[id].bestClearTicks;
      regions[id] = record; out.regions = regions;
    }
    try {
      const json = JSON.stringify(out);
      this.storage.setItem(SAVE_KEY, json);
      try { this.storage.setItem(SAVE_GOOD_KEY, json); } catch { /* 사본 실패는 주 저장 성공을 바꾸지 않는다(다음 쓰기에서 다시) */ }
      this.absorb(out);
      this.pendingGold = this.pendingClearTicks = this.pendingCombo = 0;
      this.pendingResults.clear();
      for (const id of regionIds) { const p = this.pendingRegions[id]; p.clears = p.bestClearTicks = p.bestStars = 0; }
      this.project();
      this.pending = false;
      this.lastSaveOk = true;
      if (recovering) this.recovered++;
      this.revision++;
    } catch {
      this.lastSaveOk = false;
    }
    return this.lastSaveOk;
  }
}

